import React, { memo } from 'react';
import { MenuList, MenuSection, MenuSectionHeading } from '@atlaskit/navigation-system';
import { isFedRamp } from '@atlassian/atl-context';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIsFullyExpanded } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/is-fully-expanded/index.tsx';
import LinkMenuItem from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { isCMDBEnabledInEnvironment } from '@atlassian/jira-servicedesk-cmdb-fedramp/src/index.tsx';
import IncidentManagementProductSettingNavMenuItemOnboarding from '@atlassian/jira-servicedesk-incident-management-settings/src/common/ui/setting-menu-onboarding/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { Features } from '../../common/types.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';
import messages from './messages.tsx';

type Props = {
	features: Features;
};

const ServiceManagementSection = ({ features }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();
	const { licensedProducts } = useTenantContext();
	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const isFullyExpanded = fg('fe_stakeholder_email_templates_in_jsm') && useIsFullyExpanded();
	const hasConfluence = Boolean(licensedProducts[LICENSED_PRODUCTS.CONFLUENCE]);

	const incidentManagementServiceDeskMenuItem = (
		<LinkMenuItem
			analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'incidentManagement' }}
			isSelected={currentRoute.name === 'products-jsm-incident-management-tools'}
			href="/jira/settings/products/jira-service-management-incident-management-tools"
		>
			{formatMessage(messages.incidentManagementItem)}
		</LinkMenuItem>
	);

	return (
		<MenuSection isMenuListItem={fg('platform_design_system_nav4_sidenav_a11y')}>
			<MenuSectionHeading>{formatMessage(messages.heading)}</MenuSectionHeading>
			<MenuList>
				{features.hasServiceManagementConfig && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'configuration' }}
						isSelected={currentRoute.name === 'products-jsm-configuration'}
						href="/jira/settings/products/jira-service-management-configuration"
					>
						{formatMessage(messages.configurationItem)}
					</LinkMenuItem>
				)}
				{features.hasServiceManagementEmailRequests && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'emailRequests' }}
						isSelected={currentRoute.name === 'products-jsm-email-requests'}
						href="/jira/settings/products/jira-service-management-email-requests"
					>
						{formatMessage(messages.emailRequestsItem)}
					</LinkMenuItem>
				)}
				{features.hasServiceManagementCustomerAccess && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'customerAccess' }}
						isSelected={currentRoute.name === 'products-jsm-customer-access'}
						href="/jira/settings/products/servicedesk/customer-access"
					>
						{formatMessage(messages.customerAccessItem)}
					</LinkMenuItem>
				)}
				{features.hasServiceManagementAuthenticationSettings && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'authentication' }}
						isSelected={currentRoute.name === 'products-jsm-authentication-settings'}
						href="/jira/settings/products/servicedesk/authentication"
					>
						{formatMessage(messages.authenticationItem)}
					</LinkMenuItem>
				)}
				{features.hasOrganizationsListAccess && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'organizations' }}
						isSelected={currentRoute.name === 'products-jsm-organizations'}
						href="/jira/settings/products/servicedesk/organizations"
					>
						{formatMessage(messages.organizationsItem)}
					</LinkMenuItem>
				)}
				{features.hasServiceManagementIncidentManagementTools &&
					(fg('fe_stakeholder_email_templates_in_jsm') ? (
						<IncidentManagementProductSettingNavMenuItemOnboarding
							isFullyExpanded={isFullyExpanded}
						>
							{incidentManagementServiceDeskMenuItem}
						</IncidentManagementProductSettingNavMenuItemOnboarding>
					) : (
						incidentManagementServiceDeskMenuItem
					))}
				{hasConfluence && features.hasServiceManagementKbPermissionsAccess && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'knowledgeBasePermissions' }}
						isSelected={currentRoute.name === 'products-jsm-kb-permissions'}
						href="/jira/settings/products/jira-service-management-knowledge-base-permissions"
					>
						{formatMessage(messages.knowledgeBasePermissionsItem)}
					</LinkMenuItem>
				)}
				{features.hasServiceManagementConfig && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'complianceSettings' }}
						isSelected={currentRoute.name === 'products-jsm-compliance'}
						href="/jira/settings/products/jira-service-management-compliance"
					>
						{formatMessage(messages.complianceSettingsItem)}
					</LinkMenuItem>
				)}
				{(isCMDBEnabledInEnvironment() || !isFedRamp()) &&
					!fg('aurum-677-assets-usage-migration') && (
						<LinkMenuItem
							analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'featureUsage' }}
							isSelected={currentRoute.name === 'products-jsm-feature-usage'}
							href="/jira/settings/products/servicedesk/feature-usage"
						>
							{formatMessage(messages.featureUsageItem)}
						</LinkMenuItem>
					)}
			</MenuList>
		</MenuSection>
	);
};

export default memo(ServiceManagementSection);
